import { formatISO } from 'date-fns';

import { getEnvRequired } from '../../shared/utilities/env';
import { AgentId } from '../agent/types';
import { ApiClient } from '../shared/api/client';

import { serializeDossierFromAPI, tripProjectSerializer } from './serializers';

import {
    DossierId,
    DossierJSONAPIResponse,
    MarketCode,
    Memo,
    TaskDefinition,
    TaskNote,
    TripProjectJSONAPIResponse,
} from './types';

const REQUEST_MANAGER_API_URL = getEnvRequired('REQUEST_MANAGER_API_URL');

const apiClient = new ApiClient(true);

const delay =
    async (time: number) =>
    (result: Response): Promise<Response> =>
        new Promise((resolve) => window.setTimeout(() => resolve(result), time));

const allIncludes = [
    'memo',
    'participants',
    'available-tasks',
    'customer-care-crisis-ticket',
    'contract-vouchers',
    'contract-defer-agreement',
    'pre-trip-review',
    'in-trip-review',
    'traveller-information',
    'tags',
].join(',');

export async function fetchDossier(dossierId: DossierId) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}?include=${allIncludes}`;
    const response = await apiClient.get<DossierJSONAPIResponse>(url);
    return serializeDossierFromAPI(response);
}

export async function postChangeTask(
    dossierId: DossierId,
    taskDefinition: TaskDefinition,
    taskNote: TaskNote = '',
) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/task?include=available-tasks`;
    const response = await apiClient.post<DossierJSONAPIResponse>(
        url,
        `task-definition=${taskDefinition}&task-note=${taskNote}`,
        {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    );
    await delay(350);
    return serializeDossierFromAPI(response);
}

export async function postChangeTaskNote(dossierId: DossierId, note: TaskNote) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/task/note`;
    const response = await apiClient.put<DossierJSONAPIResponse>(url, 'task-note=' + note, {
        'Content-Type': 'application/x-www-form-urlencoded',
    });
    return serializeDossierFromAPI(response);
}

export async function changeTaskDueDate(dossierId: DossierId, dueDate: string) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/task/due-date`;
    const response = await apiClient.put<DossierJSONAPIResponse>(
        url,
        JSON.stringify({ 'due-date': dueDate }),
    );
    return serializeDossierFromAPI(response);
}

export async function changeStepApi(dossierId: DossierId, step: string) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/step?include=available-tasks,tags`;
    const response = await apiClient.post<DossierJSONAPIResponse>(url, JSON.stringify({ step }));
    return serializeDossierFromAPI(response);
}

export async function postpone(dossierId: DossierId, dueDate: string) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/postpone?include=available-tasks`;
    const response = await apiClient.post<DossierJSONAPIResponse>(
        url,
        JSON.stringify({ 'due-date': dueDate }),
    );
    return serializeDossierFromAPI(response);
}

export async function reopen(dossierId: DossierId) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/reopen?include=available-tasks`;
    const response = await apiClient.post<DossierJSONAPIResponse>(url);
    return serializeDossierFromAPI(response);
}

export async function changeMemo(dossierId: DossierId, memo: Memo) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/memo`;
    await apiClient.put(url, JSON.stringify({ memo }));
}

export async function reassign(dossierId: DossierId, agentId: AgentId) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/agent`;
    await apiClient.put(url, JSON.stringify({ 'agent-id': agentId }));
}

export async function uploadVouchers(dossierId: DossierId, formData: FormData) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/add-vouchers?include=contract-vouchers`;
    const response = await apiClient.postForm<DossierJSONAPIResponse>(url, formData);
    return serializeDossierFromAPI(response);
}

export async function addVoucher(dossierId: DossierId, formData: FormData) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/voucher?include=contract-vouchers`;
    const response = await apiClient.postForm<DossierJSONAPIResponse>(url, formData);
    return serializeDossierFromAPI(response);
}

export async function deleteVoucher(dossierId: DossierId, voucherId: string) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/voucher/${voucherId}?include=contract-vouchers`;
    const response = await apiClient.delete<DossierJSONAPIResponse>(url);
    return serializeDossierFromAPI(response);
}

export async function specifyDeferAgreement(
    dossierId: DossierId,
    amount: string,
    currency: string,
    expireAt: string | null,
) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/defer/agreement?include=contract-defer-agreement`;
    const response = await apiClient.post<DossierJSONAPIResponse>(
        url,
        JSON.stringify({
            amount: amount,
            currency: currency,
            expire_at: expireAt,
        }),
    );
    return serializeDossierFromAPI(response);
}

export async function updateRequesterInformation(
    dossierId: DossierId,
    name: string,
    phoneNumber: string,
) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/project-requester`;
    await apiClient.put(
        url,
        JSON.stringify({
            name: name,
            phoneNumber: phoneNumber,
        }),
    );
}

export async function updateDatesOfServices(dossierId: DossierId, startDate: Date, endDate: Date) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/dates-of-services`;
    await apiClient.put(
        url,
        JSON.stringify({
            startDateOfServices: formatISO(startDate),
            endDateOfServices: formatISO(endDate),
        }),
    );
}

export async function updatePaxNumber(dossierId: DossierId, paxNumber: number) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/pax-number`;
    await apiClient.put(
        url,
        JSON.stringify({
            paxNumber: paxNumber,
        }),
    );
}

export async function updateHeatLevel(dossierId: DossierId, heatLevel: number) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/heat-level`;
    await apiClient.put(
        url,
        JSON.stringify({
            heatLevel: heatLevel,
        }),
    );
}

export async function validateDuplicate(dossierId: DossierId) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier-duplication-control/${dossierId}/validate-duplicate`;
    await apiClient.put(url);
}

export async function invalidateDuplicate(dossierId: DossierId) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier-duplication-control/${dossierId}/invalidate-duplicate`;
    await apiClient.put(url);
}

export async function updateBudget(dossierId: DossierId, budget: number | null) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/budget`;
    await apiClient.put(
        url,
        JSON.stringify({
            budget: budget,
        }),
    );
}

export async function revealPhoneNumber(dossierId: DossierId) {
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/reveal-phone-number`;
    await apiClient.post(url);
}

const marketCodeToBackendMarket: Record<MarketCode, string> = {
    FR: 'fr',
    ES: 'es',
    IT: 'it',
    DE: 'de',
    GB: 'en',
    NL: 'nl',
    CH: 'ch',
    CA: 'en',
    US: 'en',
    DK: 'en',
    SE: 'en',
    AT: 'de',
};

/**
 * Fetches trip project fields for a dossier from the initial request.
 *
 * Returns structured fields built from the RFQ with translated keys/values based on market.
 * Only fields with values are included in the response (backend filters empty fields).
 *
 * @param dossierId - The dossier ID
 * @param marketCode - The dossier's market code (e.g., 'FR', 'GB', 'CH')
 * @returns Serialized trip project data as a HashMap of field key to field data
 */
export async function getTripProject(dossierId: DossierId, marketCode: MarketCode) {
    const market = marketCodeToBackendMarket[marketCode];
    const url = `${REQUEST_MANAGER_API_URL}/dossier/${dossierId}/trip-project?market=${market}`;
    const response = await apiClient.get<TripProjectJSONAPIResponse>(url);

    return tripProjectSerializer(response);
}
